import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  tap,
} from 'rxjs';
import { UserDetails, UserProfile, UserLastLoginResponse } from '../interfaces/UserProfile';
import { environment } from '../../environment/environment';
import { ROLE_MAPPINGS } from '../constants/constants';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  userProfilePicture$: Observable<string>;
  userInfo$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  baseUrl = environment.backendApiConfiguration.api_url;
  constructor(private http: HttpClient) {
    this.userProfilePicture$ = this.http
      .get('https://graph.microsoft.com/v1.0/me/photos/48x48/$value', {
        responseType: 'blob',
      })
      .pipe(
        mergeMap(async (image) => {
          const imageUrl = await imageDataUrlFromBlob(image);
          return `url('${imageUrl}')`;
        })
      );
  }
  
  getUserRoles() {
    return this.http.get<string[]>(`${this.baseUrl}role`).pipe(
      map((roleResponse) => getRoleDisplayName(roleResponse)),
      shareReplay(1)
    );
  }

  getUserProfile(): Observable<UserProfile> {
    return this.http
      .get<UserProfile>(
        'https://graph.microsoft.com/v1.0/me?$select=mail,givenName,displayName,surname,id,country,department'
      )
      .pipe(tap(() => this.userInfo$.next(true)));
  }

  saveUserDetails(payload: UserDetails): Observable<any> {
    return this.http.post(`${this.baseUrl}metrics/userdetails`, payload);
  }

  saveLastLogin(lastLoginDateTime: string): Observable<any> {
    return this.http.post(`${this.baseUrl}users/saveLastLogin`, { lastLoginDateTime });
  }

  getLastLogin(): Observable<UserLastLoginResponse> {
    return this.http.get<UserLastLoginResponse>(`${this.baseUrl}users/getLastLogin`);
  }
}

function imageDataUrlFromBlob(image: Blob): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        resolve(reader.result as string);
      },
      false
    );

    reader.readAsDataURL(image);
  });
}

function getRoleDisplayName(roles: any[] = []): string[] {
  if (!roles.length) {
    return [];
  }

  const roleNames = roles
    .map(r => r?.displayName?.toLowerCase())
    .filter(Boolean);

  return ROLE_MAPPINGS
    .filter(mapping =>
      mapping.sourceRoles.some(role => roleNames.includes(role))
    )
    .map(mapping => mapping.userType);
}
